import { useCallback, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { PublicSegment } from '../../api/types'
import { NotesView } from '../../components/NotesView'
import { Player, type PlayerHandle } from '../../components/Player'
import { PublicLayout } from '../../components/PublicLayout'
import { CopyField, EmptyState, LoadingBlock } from '../../components/ui'
import { usePublicEpisode } from '../../hooks/usePublic'
import { formatDate, formatDuration } from '../../utils/format'

export function PublicEpisodePage() {
  const { workspaceSlug = '', showSlug = '', episodeSlug = '' } = useParams()
  const episode = usePublicEpisode(workspaceSlug, showSlug, episodeSlug)
  const player = useRef<PlayerHandle>(null)
  const [currentMs, setCurrentMs] = useState(0)
  const seek = useCallback((ms: number) => player.current?.seek(ms), [])
  if (episode.isPending) {
    return (
      <PublicLayout>
        <LoadingBlock />
      </PublicLayout>
    )
  }
  if (episode.isError) {
    return (
      <PublicLayout title="Not found">
        <EmptyState title="There is nothing here" body="This episode is not published, or the address is wrong." />
      </PublicLayout>
    )
  }
  const data = episode.data
  return (
    <PublicLayout title={`${data.title} · ${data.show.title}`}>
      <div className="breadcrumbs">
        <Link to={`/p/${workspaceSlug}/${showSlug}`}>{data.show.title}</Link>
      </div>
      <h1 className="public-title">{data.title}</h1>
      <p className="muted">
        {formatDate(data.published_at)}
        {data.duration_ms != null && ` · ${formatDuration(data.duration_ms)}`}
      </p>
      <div className="public-player">
        <Player ref={player} playback={data.playback} onTime={setCurrentMs} />
      </div>
      {data.summary && <p className="lead">{data.summary}</p>}
      <div className="public-columns">
        <div className="stack-lg">
          {data.show_notes && (
            <section>
              <h2>Show notes</h2>
              <NotesView text={data.show_notes} onSeek={seek} />
            </section>
          )}
          {data.transcript && (
            <section>
              <h2>Transcript</h2>
              <PublicTranscript segments={data.transcript} currentMs={currentMs} onSeek={seek} />
            </section>
          )}
        </div>
        <aside className="stack-lg">
          {data.chapters.length > 0 && (
            <section>
              <h2>Chapters</h2>
              <ol className="chapters">
                {data.chapters.map((chapter) => (
                  <li key={chapter.start_ms}>
                    <button type="button" className="tx-link" onClick={() => seek(chapter.start_ms)}>
                      {formatDuration(chapter.start_ms)}
                    </button>{' '}
                    {chapter.title}
                  </li>
                ))}
              </ol>
            </section>
          )}
          <section className="stack-sm">
            <h2>Listen and share</h2>
            <a href={data.show.feed_url}>Subscribe with the RSS feed</a>
            {data.captions && (
              <span className="muted small">
                Captions: <a href={data.captions.vtt}>WebVTT</a> · <a href={data.captions.srt}>SRT</a>
              </span>
            )}
            <CopyField label="Embed" value={data.embed_html} />
          </section>
        </aside>
      </div>
    </PublicLayout>
  )
}

function PublicTranscript({ segments, currentMs, onSeek }: { segments: PublicSegment[]; currentMs: number; onSeek: (ms: number) => void }) {
  return (
    <div className="public-transcript">
      {segments.map((segment, index) => {
        const active = currentMs >= segment.start_ms && currentMs < segment.end_ms
        return (
          <p key={index} className={active ? 'is-active' : undefined}>
            <button type="button" className="tx-link" onClick={() => onSeek(segment.start_ms)}>
              {formatDuration(segment.start_ms)}
            </button>{' '}
            {segment.speaker && <strong>{segment.speaker}: </strong>}
            {segment.text}
          </p>
        )
      })}
    </div>
  )
}
